import { Description, Label, Radio, RadioGroup } from '@headlessui/react'
import { LockClosedIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import trackEvents from '../../constants/track-events'
import {
  useLanguageContext,
  useNotificationContext,
  usePartnerContext
} from '../../context'
import { trackEvent } from '../../helpers/analytics'
import {
  CSV_PROFILES,
  buildCsv,
  defaultProfileForLanguage,
  downloadCsv
} from '../../helpers/csv'
import {
  EXPORT_COLUMN_LIMIT,
  EXPORT_GROUPS,
  buildExportColumns,
  buildExportRows,
  parseFieldDefinitions
} from '../../helpers/csv-export-columns'
import { resolveSelectedIds } from '../../helpers/csv-export-settings'
import { TABLE_IDS } from '../../helpers/table-column-settings'
import { useCsvExportSettings } from '../../hooks/use-csv-export-settings'
import { useSetState } from '../../hooks/use-set-state'
import Checkbox from '../checkbox'
import InputFormik from '../input-formik'
import Datepicker from '../react-datepicker/datepicker'
import DebouncedInput from '../react-table/debounced-input'
import Badge from '../tailwind/Badge'
import Button from '../tailwind/Button'
import CollapsibleSection from '../tailwind/CollapsibleSection'
import SlideoverWithTitleAndActions from '../tailwind/slideover-with-title-and-actions'

const I18N = 'spaces.actions.csv_export.'

// Unter dieser Menge ist eine Suche mehr Ballast als Hilfe. Die statischen
// Spalten allein bleiben darunter, sie erscheint also erst, sobald ein Kunde
// mehrere eigene Felder abfragt.
const SEARCH_THRESHOLD = 20

// Ab hier wird der Zeilenbau in Bloecke zerlegt und dazwischen an den
// Browser zurueckgegeben, sonst friert die Seite waehrend des Exports ein.
const CHUNK_SIZE = 500
const CHUNK_FROM = 2000

const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 0))

const ColumnRow = ({ column, checked, disabled, onToggle, t }) => (
  <div className='flex items-start py-1.5'>
    <Checkbox
      id={`csv-export-${column.id}`}
      label={column.label}
      description={
        column.coverage === undefined
          ? undefined
          : t(I18N + 'field_coverage', {
              filled: column.coverage,
              total: column.total
            })
      }
      checked={checked}
      disabled={disabled}
      onChange={() => onToggle(column.id)}
    />
    {column.required && (
      <span
        className='ml-2 mt-0.5 flex shrink-0 items-center'
        title={t(I18N + 'locked_hint')}
      >
        <LockClosedIcon className='size-4 text-gray-400' aria-hidden='true' />
        <span className='sr-only'>{t(I18N + 'locked_hint')}</span>
      </span>
    )}
  </div>
)

ColumnRow.propTypes = {
  column: PropTypes.object.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
}

const CSVExport = ({
  spaces,
  careers_map,
  state: { open, state },
  setState
}) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()
  const { language } = useLanguageContext()
  const { partner } = usePartnerContext()

  const { stored, persist } = useCsvExportSettings({
    tableId: TABLE_IDS.TALENTS
  })

  const [dates, setDates] = useSetState({
    // initialize datepicker with default values
    fromDate: new Date(),
    toDate: new Date(),
    minDate: new Date(),
    maxDate: new Date()
  })

  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [profile, setProfile] = useState(CSV_PROFILES.STANDARD)
  const [search, setSearch] = useState('')

  useEffect(() => {
    // if spaces change e.g. with a selection of spaces
    // update from and to date states
    if (!spaces.length) return

    const from = new Date(
      [...spaces].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      )[0].createdAt
    )

    const to = new Date(
      [...spaces].sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      )[0].updatedAt
    )

    setDates({ fromDate: from, toDate: to, minDate: from, maxDate: to })
  }, [spaces, setDates])

  const partnerFields = useMemo(
    () =>
      parseFieldDefinitions(
        partner?.app_settings?.application_input_additional_fields
      ),
    [partner]
  )

  // Nur bauen, solange das Panel offen ist. Der Lauf geht ueber alle Spaces und
  // hat auf einer geschlossenen Komponente nichts zu suchen.
  const { columns, ctx } = useMemo(() => {
    if (!open) return { columns: [], ctx: null }

    const built = buildExportColumns({
      spaces,
      careersMap: careers_map,
      partnerFields,
      t,
      language,
      origin: window.location.origin,
      partnerOriginLabel: t(I18N + 'origin_partner')
    })

    return {
      ...built,
      columns: built.columns.map((column) => ({
        ...column,
        total: spaces.length
      }))
    }
  }, [open, spaces, careers_map, partnerFields, t, language])

  // Beim Oeffnen einmal aus dem gemerkten Stand befuellen. Die Refs halten den
  // Effekt von der Spaltenidentitaet fern, sonst wuerde jede Neuberechnung die
  // Auswahl des Nutzers ueberschreiben.
  const initRef = useRef({})
  initRef.current = { columns, stored, language }

  useEffect(() => {
    if (!open) return

    const current = initRef.current

    setSelectedIds(
      resolveSelectedIds({ stored: current.stored, columns: current.columns })
    )
    setProfile(
      current.stored?.profile || defaultProfileForLanguage(current.language)
    )
    setSearch('')
  }, [open])

  const initialValues = useMemo(() => {
    const currentDate = new Date()

    const year = currentDate.getFullYear()
    const month = currentDate.getMonth() + 1 // adding 1 because months are zero-indexed
    const day = currentDate.getDate()

    return {
      filename: `talents-export_${year}-${month < 10 ? '0' + month : month}-${
        day < 10 ? '0' + day : day
      }`,
      only_completed_talents: false
    }
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      filename: Yup.string().required(t(I18N + 'validation_required'))
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true)

      const selectedColumns = columns.filter(({ id }) => selectedIds.has(id))

      const filtered_spaces = spaces
        .filter((space) => {
          if (values.only_completed_talents) {
            return space.progress === 100
          }

          return true
        })
        .filter((space) => {
          if (state === 'all') {
            // be aware of datepicker values

            const createdAt = new Date(space.createdAt)
            const updatedAt = new Date(space.updatedAt)

            // createdAt should be bigger than fromDate
            // updatedAt should be smaller than toData
            return createdAt >= dates.fromDate && updatedAt <= dates.toDate
          }

          return true
        })

      const rows = []
      const chunked = filtered_spaces.length > CHUNK_FROM

      for (let index = 0; index < filtered_spaces.length; index += CHUNK_SIZE) {
        rows.push(
          ...buildExportRows({
            spaces: filtered_spaces.slice(index, index + CHUNK_SIZE),
            columns: selectedColumns,
            ctx,
            profile
          })
        )

        if (chunked) await yieldToBrowser()
      }

      downloadCsv({
        content: buildCsv({
          headers: selectedColumns.map(({ label }) => label),
          rows,
          profile
        }),
        filename: values.filename
      })

      persist({
        selectedIds: selectedColumns.map(({ id }) => id),
        resolvableIds: columns.map(({ id }) => id),
        profile
      })

      setSubmitting(false)

      setState({ open: false })
      success(t(I18N + 'success_message'))
      trackEvent(trackEvents.COMPLETE_CSV_EXPORT, {
        columns: selectedColumns.length,
        rows: rows.length,
        profile
      })

      // await panel transition
      setTimeout(() => {
        resetForm()
        setState({ state: null })
      }, 1000 * 0.8)
    }
  })

  const close = () => {
    trackEvent(trackEvents.ABORT_CSV_EXPORT)
    setState({ open: false })

    // await panel transition
    setTimeout(() => {
      formik.resetForm()
      setState({ state: null })
    }, 1000 * 0.8)
  }

  const { fromDate, toDate, minDate, maxDate } = dates

  const atLimit = selectedIds.size >= EXPORT_COLUMN_LIMIT

  const toggleColumn = (id) => {
    setSelectedIds((previous) => {
      const next = new Set(previous)

      if (next.has(id)) next.delete(id)
      else if (next.size < EXPORT_COLUMN_LIMIT) next.add(id)

      return next
    })
  }

  const setGroup = (items, enabled) => {
    setSelectedIds((previous) => {
      const next = new Set(previous)

      items.forEach((column) => {
        if (column.required) return

        if (!enabled) next.delete(column.id)
        else if (next.size < EXPORT_COLUMN_LIMIT) next.add(column.id)
      })

      return next
    })
  }

  const reset = () => {
    setSelectedIds(resolveSelectedIds({ stored: null, columns }))
    setProfile(defaultProfileForLanguage(language))
  }

  const query = search.trim().toLowerCase()

  const groups = useMemo(() => {
    const matches = ({ label, origin }) =>
      !query ||
      label.toLowerCase().includes(query) ||
      (origin || '').toLowerCase().includes(query)

    return EXPORT_GROUPS.map((group) => {
      const items = columns.filter(
        (column) => column.group === group.id && matches(column)
      )

      // Innerhalb der Zusatzfelder nach Stelle untergliedern, damit sichtbar
      // ist, warum ein Label zweimal vorkommt.
      const sections = []
      const byOrigin = new Map()

      items.forEach((column) => {
        const key = column.origin || ''

        if (!byOrigin.has(key)) {
          const section = { origin: column.origin, items: [] }
          byOrigin.set(key, section)
          sections.push(section)
        }

        byOrigin.get(key).items.push(column)
      })

      return { ...group, items, sections }
    })
  }, [columns, query])

  const hasMatches = groups.some(({ items }) => items.length)
  const showSearch = columns.length > SEARCH_THRESHOLD
  const recommended = defaultProfileForLanguage(language)

  const formats = [
    {
      value: CSV_PROFILES.EXCEL,
      label: t(I18N + 'format_excel_label'),
      description: t(I18N + 'format_excel_description')
    },
    {
      value: CSV_PROFILES.STANDARD,
      label: t(I18N + 'format_standard_label'),
      description: t(I18N + 'format_standard_description')
    }
  ]

  const activeFormat = formats.find(({ value }) => value === profile)

  return (
    <SlideoverWithTitleAndActions
      open={open}
      setOpen={close}
      widthClassName='max-w-xl'
      title={t(I18N + 'modal_title')}
      description={t(I18N + 'modal_description', { number: spaces.length })}
      actions={
        <div className='flex flex-shrink-0 justify-end space-x-2 px-4 py-4 sm:px-6'>
          <Button.SecondaryXL
            text={t(I18N + 'modal_cancel_action')}
            onClick={close}
          />
          <Button.PrimaryXL
            text={t(I18N + 'modal_submit_action')}
            onClick={formik.handleSubmit}
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid}
          />
        </div>
      }
    >
      <div className='px-4 sm:px-6'>
        {/* Sekundaere Einstellungen liegen zugeklappt, damit die Spaltenliste
            den Platz bekommt. Die Kopfzeile zeigt den Stand ohne Aufklappen. */}
        <CollapsibleSection
          className='py-4'
          title={
            <span className='flex min-w-0 flex-col items-start text-left'>
              <span>{t(I18N + 'export_settings_title')}</span>
              <span className='truncate text-xs font-normal text-gray-500'>
                {`${activeFormat?.label} · ${
                  formik.values.filename || formik.initialValues.filename
                }`}
              </span>
            </span>
          }
        >
          <InputFormik
            {...{
              id: 'filename',
              label: t(I18N + 'filename_label'),
              placeholder: formik.initialValues.filename,
              formik
            }}
          />

          <div className='mt-5'>
            <div className='text-sm font-medium text-gray-700'>
              {t(I18N + 'format_title')}
            </div>
            <RadioGroup
              value={profile}
              onChange={setProfile}
              aria-label={t(I18N + 'format_title')}
              className='mt-2 grid grid-cols-1 gap-2'
            >
              {formats.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  className={({ checked }) =>
                    clsx(
                      'group relative flex cursor-pointer flex-col rounded-lg border bg-slate-50 px-3 py-2.5 pr-10 text-left',
                      'focus:outline-none data-[focus]:ring-2 data-[focus]:ring-blue-600 data-[focus]:ring-offset-2',
                      checked
                        ? 'border-primaryBlue ring-1 ring-primaryBlue'
                        : 'border-slate-200 hover:border-primaryBlue'
                    )
                  }
                >
                  {({ checked }) => (
                    <>
                      {checked && (
                        <CheckCircleIcon
                          aria-hidden='true'
                          className='absolute right-3 top-2.5 size-5 text-primaryBlue'
                        />
                      )}
                      <Label className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold text-gray-900'>
                        {option.label}
                        {option.value === recommended && (
                          <span className='shrink-0 whitespace-nowrap'>
                            <Badge
                              type='basic'
                              color='green'
                              text={t(I18N + 'format_recommended_badge')}
                            />
                          </span>
                        )}
                      </Label>
                      <Description className='mt-0.5 text-xs text-gray-600'>
                        {option.description}
                      </Description>
                    </>
                  )}
                </Radio>
              ))}
            </RadioGroup>
          </div>

          {state === 'all' && (
            <>
              <div className='mt-5 flex'>
                <Checkbox
                  id='only_completed_talents'
                  onChange={formik.handleChange}
                  checked={formik.values.only_completed_talents}
                  label={t(I18N + 'only_completed_talents_title')}
                />
              </div>
              <div className='mt-5'>
                <div className='text-sm font-medium text-gray-700'>
                  {t(I18N + 'from_to_title')}
                </div>
                <div className='mt-2 flex gap-x-2'>
                  <Datepicker
                    selected={fromDate}
                    onChange={(date) => setDates({ fromDate: date })}
                    minDate={minDate}
                    maxDate={toDate}
                  />
                  <Datepicker
                    selected={toDate}
                    onChange={(date) => setDates({ toDate: date })}
                    minDate={fromDate}
                    maxDate={maxDate}
                  />
                </div>
              </div>
            </>
          )}
        </CollapsibleSection>
      </div>

      {/* Bleibt beim Scrollen der Liste stehen, sonst sind Zaehler und Suche
          bei vielen Zusatzfeldern weg. */}
      <div className='sticky top-0 z-10 border-y border-gray-200 bg-white px-4 py-3 sm:px-6'>
        <div className='flex items-center justify-between gap-x-2'>
          <div className='flex items-center gap-x-2'>
            <span className='text-sm font-medium text-gray-900'>
              {t(I18N + 'data_colums_title')}
            </span>
            <span className='rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700'>
              {t(I18N + 'selected_count', {
                selected: selectedIds.size,
                max: EXPORT_COLUMN_LIMIT
              })}
            </span>
          </div>
          <Button.Text
            text={t(I18N + 'reset_action')}
            onClick={reset}
            minWidth=''
          />
        </div>

        <p className='sr-only' aria-live='polite'>
          {t(I18N + 'selected_count', {
            selected: selectedIds.size,
            max: EXPORT_COLUMN_LIMIT
          })}
        </p>

        {showSearch && (
          <div className='mt-2'>
            <DebouncedInput
              id='csv-export-search'
              initialValue={search}
              onChange={setSearch}
              placeholder={t(I18N + 'search_placeholder')}
              debounce={150}
            />
          </div>
        )}

        {atLimit && (
          <p className='mt-2 text-xs text-gray-500'>
            {t(I18N + 'selected_limit_hint', { max: EXPORT_COLUMN_LIMIT })}
          </p>
        )}
      </div>

      <div className='px-4 pb-6 sm:px-6'>
        {groups.map((group) => {
          const isAdditional = group.id === 'additional'

          if (!group.items.length && !(isAdditional && !query)) return null

          const allSelected =
            group.items.length > 0 &&
            group.items.every(({ id }) => selectedIds.has(id))

          const groupLabel = t(group.labelKey)

          // Ein Schalter statt zwei. Die Beschriftung nennt die Aktion, die der
          // Klick ausloest, und halbiert die Zahl der Bedienelemente. Die Keys
          // stehen ausgeschrieben da, damit die Suche nach toten Keys greift.
          const toggleLabel = allSelected
            ? t(I18N + 'group_select_none')
            : t(I18N + 'group_select_all')
          const toggleAriaLabel = allSelected
            ? t(I18N + 'group_select_none_sr', { group: groupLabel })
            : t(I18N + 'group_select_all_sr', { group: groupLabel })

          return (
            <fieldset key={group.id} className='mt-5 first:mt-4'>
              {/* legend muss direktes Kind des fieldset sein, sonst ist es
                  fuer Screenreader kein Gruppenname mehr. Die sichtbare
                  Zeile daneben traegt deshalb aria-hidden. */}
              <legend className='sr-only'>{groupLabel}</legend>
              <div className='flex items-center justify-between gap-x-2'>
                <span
                  aria-hidden='true'
                  className='text-xs font-semibold uppercase tracking-wider text-gray-500'
                >
                  {groupLabel}
                </span>
                {group.items.length > 0 && (
                  <Button.Text
                    text={toggleLabel}
                    ariaLabel={toggleAriaLabel}
                    onClick={() => setGroup(group.items, !allSelected)}
                    minWidth=''
                  />
                )}
              </div>

              {isAdditional && !group.items.length && (
                <p className='mt-2 text-sm text-gray-500'>
                  {t(I18N + 'additional_empty')}
                </p>
              )}

              <div className='mt-1'>
                {group.sections.map((section, index) => (
                  <div key={section.origin || index}>
                    {isAdditional && section.origin && (
                      <p className='mb-1 mt-3 text-xs font-medium text-gray-500'>
                        {section.origin}
                      </p>
                    )}
                    {section.items.map((column) => (
                      <ColumnRow
                        key={column.id}
                        column={column}
                        t={t}
                        checked={selectedIds.has(column.id)}
                        disabled={
                          !!column.required ||
                          (atLimit && !selectedIds.has(column.id))
                        }
                        onToggle={toggleColumn}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </fieldset>
          )
        })}

        {!hasMatches && query && (
          <p className='mt-4 text-sm text-gray-500'>
            {t(I18N + 'search_empty', { query: search.trim() })}
          </p>
        )}
      </div>
    </SlideoverWithTitleAndActions>
  )
}

CSVExport.propTypes = {
  spaces: PropTypes.array.isRequired,
  careers_map: PropTypes.object.isRequired,
  state: PropTypes.object.isRequired,
  setState: PropTypes.func.isRequired
}

export default CSVExport
